/**
 * <ContentContainer> — the platform-wide reading-width wrapper.
 *
 * Every long-form / static-content page mounts this component instead of
 * managing its own `max-w-*`, padding and overflow rules. Centralising
 * the contract here means future pages inherit the same constraints
 * without each author having to remember them.
 *
 * Variants
 * --------
 *  • prose (default) — ~768px column. Optimised for paragraph reading
 *    (60–80 chars/line). Use for CMS pages, FAQ, T&C, Privacy, Help,
 *    Safety guidelines, About, blog posts, user guides.
 *  • wide            — ~1200px column. Use for dashboards / app pages
 *    that mix text and dense UI (admin panels embedded in marketing
 *    layouts, multi-column landing sections, etc.).
 *  • narrow          — ~640px. Auth screens, single-form modals
 *    rendered as a page.
 *  • bleed           — full viewport width (skips max-width). For pages
 *    that need an edge-to-edge hero plus a constrained text block: use
 *    `bleed` on the outer container and nest a `prose` ContentContainer
 *    for the readable section.
 *
 * Behaviour
 * ---------
 *  • Always centres horizontally (`mx-auto`).
 *  • Responsive padding: 16px mobile / 24px tablet / 32px desktop.
 *  • `box-sizing: border-box` is enforced globally (see `index.css`).
 *  • Long words / URLs / inline code wrap rather than escape the
 *    container — see the global `overflow-wrap: break-word` block in
 *    `index.css` plus this component's inline guarantee for safety.
 *  • Horizontal scroll is impossible: `html, body { overflow-x: hidden }`
 *    is the platform-wide kill switch (also in `index.css`). Inside this
 *    component, `overflow-x-clip` enforces the same at the column.
 *
 * Typography helpers (composable, opt-in)
 * --------------------------------------
 *  • <ContentArticle> wraps children in a Tailwind-Typography `<article
 *    class="prose">` so editorially-authored HTML (DOMPurify-sanitized)
 *    gets sensible defaults — headings hierarchy, paragraph spacing,
 *    link colour, list bullets, blockquotes, etc. — without per-page
 *    overrides.
 */
const VARIANT_TO_MAXW = {
  prose:  'max-w-3xl',      // ≈ 768px
  wide:   'max-w-screen-xl',// ≈ 1280px
  narrow: 'max-w-xl',       // ≈ 576px
  bleed:  'max-w-none',     // no constraint
};

export const ContentContainer = ({
  children,
  variant = 'prose',
  className = '',
  as: Tag = 'div',
  testId,
  padding = 'normal', // 'normal' | 'tight' | 'none'
}) => {
  const maxW = VARIANT_TO_MAXW[variant] || VARIANT_TO_MAXW.prose;
  const padClass =
    padding === 'none'  ? '' :
    padding === 'tight' ? 'px-3 sm:px-4 lg:px-6 py-4' :
                          'px-4 sm:px-6 lg:px-8 py-8 md:py-12';
  return (
    <Tag
      className={`content-container w-full ${maxW} mx-auto ${padClass} ${className}`}
      data-testid={testId}
      data-variant={variant}
    >
      {children}
    </Tag>
  );
};

/**
 * <ContentArticle> — opinionated wrapper for editorial HTML.
 *
 * Pair with `dangerouslySetInnerHTML` or `html-react-parser` output.
 * Includes the page <h1> title and the body in one tidy block; pass
 * `title={null}` if the title is rendered upstream.
 */
export const ContentArticle = ({ title, children, className = '', testId }) => (
  <ContentContainer variant="prose" as="main" testId={testId}>
    {title && (
      <h1
        className="text-3xl md:text-4xl font-bold font-heading text-text_main mb-6 break-words"
        data-testid={testId ? `${testId}-title` : undefined}
      >
        {title}
      </h1>
    )}
    <article
      className={`prose prose-sm md:prose-base max-w-none ${className}`}
      data-testid={testId ? `${testId}-body` : undefined}
    >
      {children}
    </article>
  </ContentContainer>
);
